/**
 * Offline-first authentication store.
 *
 * Everything the auth flow needs lives in the local IndexedDB: the user
 * directory, the devices bound to each user, the single active session and the
 * salted digest of the local 4-digit PIN. No network call is required to sign
 * in once a device has been approved.
 *
 * One rule sits in front of all of it: **only an operator holds a session.**
 * `startSession()` refuses any account that is not one of the two tiers in
 * `src/core/operator-access.ts`, so a נהג cannot be signed in from the UI, from
 * a restored session, or by a caller that skipped the flow entirely. Drivers are
 * on the roster and on the dispatch order; they are not on the login screen.
 */
import { db } from './index';
import { recordUserAdminEvent } from './audit-store';
import { MOCK_DEFAULT_PINS, MOCK_USERS, SUPER_ADMIN_ID } from '../core/mock-users';
import { ACCESS_LEVEL_LABELS, ROLE_ACCESS_LEVELS } from '../core/access-levels';
import { collectDeviceSignals, computeDeviceFingerprint, describeDevice } from '../core/device';
import { hashPin, isValidPin, verifyPin } from '../core/pin';
import { normalizePhone } from '../core/phone';
import { SESSION_ID, createSession, isSessionExpired, isSessionValidFor, touchSession } from '../core/session';
import {
  canResumeWithoutUnlock,
  clearSessionCache,
  lockSessionCache,
  readSessionCache,
  touchSessionCache,
  writeSessionCache,
} from '../core/session-cache';
import { PERMISSION_LABELS, assertPermission } from '../core/permissions';
import { NotAnOperatorError, isOperator } from '../core/operator-access';
import { DEFAULT_USER_PREFERENCES } from '../types/user';
import type {
  AuthSession,
  Device,
  LocalPinRecord,
  UnlockMethod,
  User,
  UserPermission,
  UserPreferences,
  UserRole,
  UserStatus,
} from '../types/user';

const nowIso = (now: number = Date.now()): string => new Date(now).toISOString();

// ── User directory ───────────────────────────────────────────────────

/** Identity fields the seed owns — everything else is runtime state. */
const IDENTITY_FIELDS = [
  'phone',
  'fullName',
  'personalNumber',
  'role',
  'company',
  'platoon',
] as const;

type SeedIdentity = Pick<User, (typeof IDENTITY_FIELDS)[number]>;

const hasSeedIdentity = (stored: User, seed: User): boolean =>
  IDENTITY_FIELDS.every((field) => stored[field] === seed[field]);

const seedIdentity = (seed: User): SeedIdentity => ({
  phone: seed.phone,
  fullName: seed.fullName,
  personalNumber: seed.personalNumber,
  role: seed.role,
  company: seed.company,
  platoon: seed.platoon,
});

/**
 * Populates the directory with the seeded owner account, and re-applies its
 * identity to a directory seeded by an older build. Runtime state an admin may
 * have changed — approval, status, permission overrides — is left untouched,
 * and so is an account the operator has since edited themselves: once
 * `profileUpdatedAt` is set, their own name and phone win over the seed.
 */
export async function seedMockUsers(): Promise<void> {
  /** Accounts whose identity just changed under an existing installation. */
  const renamed: string[] = [];

  for (const seed of MOCK_USERS) {
    const stored = await db.users.get(seed.id);
    if (!stored) {
      await db.users.put({ ...seed });
      continue;
    }
    if (stored.profileUpdatedAt || hasSeedIdentity(stored, seed)) {
      // No screen edits the personal number, so it is the seed's alone even on
      // an account whose own details the operator has since changed.
      if (stored.personalNumber !== seed.personalNumber) {
        await db.users.put({ ...stored, personalNumber: seed.personalNumber });
      }
      continue;
    }
    await db.users.put({ ...stored, ...seedIdentity(seed) });
    renamed.push(seed.id);
  }

  await seedDefaultPins(renamed);
}

/**
 * Provisions the default quick-unlock PIN for the accounts that ship with one.
 * A PIN the user chose is never overwritten — except for an account that was
 * just re-identified, where the old code belonged to the previous persona.
 */
export async function seedDefaultPins(resetFor: readonly string[] = []): Promise<void> {
  for (const [userId, pin] of Object.entries(MOCK_DEFAULT_PINS)) {
    if (!resetFor.includes(userId) && (await hasLocalPin(userId))) continue;
    await setLocalPin(userId, pin);
  }
}

export async function listUsers(): Promise<User[]> {
  return db.users.toArray();
}

export async function getUserById(userId: string): Promise<User | null> {
  return (await db.users.get(userId)) ?? null;
}

export async function findUserByPhone(phone: string): Promise<User | null> {
  const normalized = normalizePhone(phone);
  if (!normalized) return null;
  return (await db.users.where('phone').equals(normalized).first()) ?? null;
}

// ── Own profile ──────────────────────────────────────────────────────

/** What "עריכת פרטים אישיים" may change — and nothing else. */
export interface ProfileDraft {
  fullName?: string;
  phone?: string;
  preferences?: Partial<UserPreferences>;
}

/**
 * Edits the signed-in operator's own details.
 *
 * No permission is asked for: every account owns its own name, phone and
 * display choices. What the form may *not* reach is the account's standing —
 * role, status, approval and permission flags are set from the management
 * screens by somebody else, and are copied through untouched here.
 *
 * Writing this also stamps `profileUpdatedAt`, which is what keeps the seed
 * from putting the shipped name back on the next boot.
 */
export async function updateOwnProfile(actor: User, draft: ProfileDraft): Promise<User> {
  const stored = (await getUserById(actor.id)) ?? actor;

  const fullName = draft.fullName === undefined ? stored.fullName : draft.fullName.trim();
  if (!fullName) throw new Error('שם מלא הוא שדה חובה');

  let phone = stored.phone;
  if (draft.phone !== undefined) {
    phone = normalizePhone(draft.phone);
    if (!phone) throw new Error('מספר טלפון אינו תקין');
    const owner = await findUserByPhone(phone);
    if (owner && owner.id !== stored.id) throw new Error('מספר הטלפון כבר משויך למשתמש אחר');
  }

  const updated: User = {
    ...stored,
    fullName,
    phone,
    preferences: {
      ...DEFAULT_USER_PREFERENCES,
      ...(stored.preferences ?? {}),
      ...(draft.preferences ?? {}),
    },
    profileUpdatedAt: nowIso(),
  };
  await db.users.put(updated);
  return updated;
}

/** The display choices in force for an account, defaults filled in. */
export function preferencesOf(user: User | null | undefined): UserPreferences {
  return { ...DEFAULT_USER_PREFERENCES, ...(user?.preferences ?? {}) };
}

// ── The user directory ("ניהול הרשאות ומשתמשים") ─────────────────────

/**
 * Raised when a directory action would leave the unit with no way back in — the
 * owner account demoted, suspended, or locked out of its own directory.
 *
 * It is not a permission failure: the מנהל על genuinely holds `MANAGE_USERS`,
 * and is being stopped from turning it off on themselves rather than from using
 * it. The message is the sentence the screen shows.
 */
export class DirectoryLockoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DirectoryLockoutError';
  }
}

/**
 * Refuses any change to the seeded owner's standing.
 *
 * `MANAGE_USERS` and `RESET_USER_PIN` are reserved to super-admins, so an
 * installation whose only super-admin was demoted or suspended has nobody left
 * who can undo it — and no screen that can reach the directory to try. The
 * owner's role, approval and status are therefore fixed; everything else about
 * the account (their name, their phone, their own PIN) stays theirs to change.
 */
function assertOwnerStandingUntouched(user: User, what: string): void {
  if (user.id !== SUPER_ADMIN_ID) return;
  throw new DirectoryLockoutError(
    `לא ניתן ${what} של חשבון מנהל העל — זהו החשבון היחיד שרשאי לנהל את המשתמשים והקודים.`,
  );
}

/** Flips a single custom configuration flag for an admin account. */
export async function setPermissionOverride(
  actor: User,
  userId: string,
  action: UserPermission,
  value: boolean | null,
): Promise<User | null> {
  assertPermission(actor, 'MANAGE_USERS');
  const user = await getUserById(userId);
  if (!user) return null;
  const overrides = { ...(user.permissionOverrides ?? {}) };
  if (value === null) delete overrides[action];
  else overrides[action] = value;
  const updated: User = { ...user, permissionOverrides: overrides };
  await db.users.put(updated);
  await recordUserAdminEvent(
    actor,
    'USER_PERMISSION_CHANGED',
    updated,
    `${updated.fullName} — הרשאת "${PERMISSION_LABELS[action]}" ${
      value === null ? 'הוחזרה לברירת המחדל של התפקיד' : value ? 'ניתנה' : 'נשללה'
    }`,
    [
      {
        field: `permission:${action}`,
        label: PERMISSION_LABELS[action],
        from: describeOverride(user.permissionOverrides?.[action]),
        to: describeOverride(value),
      },
    ],
  );
  return updated;
}

/** How a configuration flag reads in the ledger. */
const describeOverride = (value: boolean | null | undefined): string =>
  value === true ? 'ניתנה' : value === false ? 'נשללה' : 'ברירת מחדל';

export async function setUserApproval(
  actor: User,
  userId: string,
  isApproved: boolean,
): Promise<User | null> {
  assertPermission(actor, 'MANAGE_USERS');
  const user = await getUserById(userId);
  if (!user) return null;
  if (user.isApproved === isApproved) return user;
  assertOwnerStandingUntouched(user, 'לשנות את מצב הגישה');
  const updated: User = {
    ...user,
    isApproved,
    status: isApproved ? 'active' : 'suspended',
  };
  await db.users.put(updated);
  await recordUserAdminEvent(
    actor,
    'USER_ACCESS_CHANGED',
    updated,
    `${updated.fullName} — הגישה ${isApproved ? 'הופעלה' : 'הושבתה'}`,
    [
      {
        field: 'access',
        label: 'גישה למערכת',
        from: describeAccess(user),
        to: describeAccess(updated),
      },
    ],
  );
  // A suspended account must not keep walking around on the device it was last
  // used from: the standing session dies with the access.
  if (!isApproved) await endSessionsFor(userId);
  return updated;
}

/** How an account's standing reads in the ledger and on the directory row. */
export const describeAccess = (user: Pick<User, 'isApproved' | 'status'>): string =>
  user.isApproved && user.status === 'active' ? 'פעילה' : 'מושבתת';

/**
 * Moves an account between the three access levels — the "רמת הרשאה" control on
 * the management screen.
 *
 * Reserved to super-admins twice over: `MANAGE_USERS` is a super-admin-only
 * permission to begin with, and the owner's own role is refused outright, so the
 * level that hands out levels can never be given away by accident.
 */
export async function setUserRole(
  actor: User,
  userId: string,
  role: UserRole,
): Promise<User | null> {
  assertPermission(actor, 'MANAGE_USERS');
  const user = await getUserById(userId);
  if (!user) return null;
  if (user.role === role) return user;
  assertOwnerStandingUntouched(user, 'לשנות את רמת ההרשאה');
  const updated: User = { ...user, role };
  await db.users.put(updated);
  await recordUserAdminEvent(
    actor,
    'USER_ROLE_CHANGED',
    updated,
    `${updated.fullName} — רמת ההרשאה שונתה ל״${ACCESS_LEVEL_LABELS[ROLE_ACCESS_LEVELS[role]]}״`,
    [
      {
        field: 'role',
        label: 'רמת הרשאה',
        from: ACCESS_LEVEL_LABELS[ROLE_ACCESS_LEVELS[user.role]],
        to: ACCESS_LEVEL_LABELS[ROLE_ACCESS_LEVELS[role]],
      },
    ],
  );
  return updated;
}

/** Sets the lifecycle state directly — used when approving a pending account. */
export async function setUserStatus(
  actor: User,
  userId: string,
  status: UserStatus,
): Promise<User | null> {
  assertPermission(actor, 'MANAGE_USERS');
  const user = await getUserById(userId);
  if (!user) return null;
  if (user.status === status) return user;
  assertOwnerStandingUntouched(user, 'לשנות את מצב החשבון');
  const updated: User = { ...user, status, isApproved: status === 'active' ? true : user.isApproved };
  await db.users.put(updated);
  await recordUserAdminEvent(
    actor,
    'USER_ACCESS_CHANGED',
    updated,
    `${updated.fullName} — מצב החשבון שונה`,
    [{ field: 'status', label: 'מצב חשבון', from: describeAccess(user), to: describeAccess(updated) }],
  );
  if (status !== 'active') await endSessionsFor(userId);
  return updated;
}

/** What the management screen must supply to open an account. */
export interface TeamMemberDraft {
  fullName: string;
  phone: string;
  personalNumber: string;
  role: UserRole;
  company?: string;
  platoon?: string;
  /** The 4-digit code the member signs in with, set by the מנהל על. */
  pin?: string;
}

/**
 * Opens an account for somebody on the roster — the only way a second user ever
 * reaches the directory, which ships holding the owner alone.
 *
 * The account is created approved and active: a מנהל על adding a member has
 * already vetted them, and an account that had to be approved a second time from
 * the same screen would be a step with no decision in it. Their device is a
 * separate question and is still approved on its own.
 */
export async function createTeamMember(actor: User, draft: TeamMemberDraft): Promise<User> {
  assertPermission(actor, 'MANAGE_USERS');

  const fullName = draft.fullName.trim();
  if (!fullName) throw new Error('שם מלא הוא שדה חובה');

  const phone = normalizePhone(draft.phone);
  if (!phone) throw new Error('מספר טלפון אינו תקין');
  if (await findUserByPhone(phone)) throw new Error('מספר הטלפון כבר משויך למשתמש אחר');

  const personalNumber = draft.personalNumber.trim();
  if (!personalNumber) throw new Error('מספר אישי הוא שדה חובה');

  if (draft.pin !== undefined && !isValidPin(draft.pin)) {
    throw new Error('קוד הכניסה חייב להיות 4 ספרות');
  }

  const user: User = {
    id: `usr-${crypto.randomUUID()}`,
    phone,
    fullName,
    personalNumber,
    role: draft.role,
    company: draft.company?.trim() || actor.company,
    platoon: draft.platoon?.trim() || '',
    status: 'active',
    isApproved: true,
    createdAt: nowIso(),
  };
  await db.users.add(user);
  if (draft.pin !== undefined) await setLocalPin(user.id, draft.pin);

  await recordUserAdminEvent(
    actor,
    'USER_CREATED',
    user,
    `${user.fullName} — נפתח חשבון ברמת "${ACCESS_LEVEL_LABELS[ROLE_ACCESS_LEVELS[user.role]]}"`,
    [
      {
        field: 'role',
        label: 'רמת הרשאה',
        from: '—',
        to: ACCESS_LEVEL_LABELS[ROLE_ACCESS_LEVELS[user.role]],
      },
    ],
  );
  return user;
}

/**
 * Sets or resets the 4-digit code another account signs in with.
 *
 * Guarded by `RESET_USER_PIN`, which is super-admin-only — a commander runs the
 * day and still cannot change the code somebody else signs in with. The code
 * itself is never stored: `setLocalPin()` keeps a salted PBKDF2 digest, so the
 * ledger records *that* it was reset and by whom, and nothing more.
 */
export async function resetUserPin(actor: User, userId: string, pin: string): Promise<User | null> {
  assertPermission(actor, 'RESET_USER_PIN');
  if (!isValidPin(pin)) throw new Error('קוד הכניסה חייב להיות 4 ספרות');
  const user = await getUserById(userId);
  if (!user) return null;
  await setLocalPin(userId, pin);
  await recordUserAdminEvent(
    actor,
    'USER_PIN_RESET',
    user,
    `${user.fullName} — קוד הכניסה אופס על ידי מנהל על`,
  );
  return user;
}

/**
 * Drops the standing session when it belongs to the account being closed.
 *
 * Only one session exists per installation, so this is the device the operator
 * is holding: an account suspended on the מנהל על's own phone must not stay
 * signed in on it.
 */
async function endSessionsFor(userId: string): Promise<void> {
  const session = await db.sessions.get(SESSION_ID);
  if (session?.userId === userId) await clearSession();
}

// ── Devices ──────────────────────────────────────────────────────────

export interface DeviceRegistration {
  device: Device;
  fingerprint: string;
  /** The device was seen for the first time on this sign-in. */
  isNew: boolean;
}

export interface RegisterDeviceOptions {
  /**
   * The account has already been vetted by somebody other than this device, so
   * the pending-approval gate has nothing left to decide.
   *
   * Set by the centralised sign-in (`src/db/cloud-auth-store.ts`): the phone
   * and the group password were checked against the D1 directory, and the
   * directory said yes. The gate exists because a local sign-in proves nothing
   * a device did not tell itself — once the server is the one answering, making
   * the operator find a מנהל על to approve the same phone again is a step with
   * no decision left in it.
   */
  trusted?: boolean;
}

/**
 * Binds the current browser to a user, creating the device record on first
 * sight. A super-admin's own device is trusted immediately (bootstrap), a
 * device the central directory vouched for is trusted with it, and every other
 * device stays pending until a super-admin approves it.
 */
export async function registerDevice(
  user: User,
  options: RegisterDeviceOptions = {},
): Promise<DeviceRegistration> {
  const signals = collectDeviceSignals();
  const fingerprint = await computeDeviceFingerprint(signals);
  const stamp = nowIso();

  const existing = await db.devices
    .where('[userId+deviceFingerprint]')
    .equals([user.id, fingerprint])
    .first();

  if (existing) {
    // A device left pending by an earlier local attempt is released by a
    // sign-in the directory itself answered — the same decision, arriving from
    // the one place entitled to make it.
    const approve = existing.isApproved || Boolean(options.trusted);
    const device: Device = {
      ...existing,
      lastActive: stamp,
      isApproved: approve,
      approvedAt: existing.approvedAt ?? (approve ? stamp : null),
    };
    await db.devices.put(device);
    return { device, fingerprint, isNew: false };
  }

  const autoApprove = user.role === 'super_admin' || Boolean(options.trusted);
  const device: Device = {
    id: crypto.randomUUID(),
    userId: user.id,
    deviceFingerprint: fingerprint,
    deviceName: describeDevice(signals),
    isApproved: autoApprove,
    lastActive: stamp,
    approvedAt: autoApprove ? stamp : null,
    approvedBy: autoApprove ? user.id : undefined,
    createdAt: stamp,
  };
  await db.devices.put(device);
  return { device, fingerprint, isNew: true };
}

export async function getDevice(deviceId: string): Promise<Device | null> {
  return (await db.devices.get(deviceId)) ?? null;
}

export async function listDevices(userId?: string): Promise<Device[]> {
  if (userId) return db.devices.where('userId').equals(userId).toArray();
  return db.devices.toArray();
}

/** Devices waiting for a super-admin decision, newest first. */
export async function listPendingDevices(): Promise<Device[]> {
  const devices = await db.devices.toArray();
  return devices
    .filter((device) => !device.isApproved)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function approveDevice(actor: User, deviceId: string): Promise<Device | null> {
  assertPermission(actor, 'APPROVE_DEVICE');
  const device = await getDevice(deviceId);
  if (!device) return null;
  const updated: Device = {
    ...device,
    isApproved: true,
    approvedAt: nowIso(),
    approvedBy: actor.id,
  };
  await db.devices.put(updated);
  return updated;
}

/** Revokes a device; if it is the one holding the session, the session dies. */
export async function disconnectDevice(actor: User, deviceId: string): Promise<boolean> {
  assertPermission(actor, 'DISCONNECT_DEVICE');
  const device = await getDevice(deviceId);
  if (!device) return false;
  await db.devices.delete(deviceId);
  const session = await db.sessions.get(SESSION_ID);
  if (session?.deviceId === deviceId) await clearSession();
  return true;
}

// ── Session ──────────────────────────────────────────────────────────

/**
 * Persists the session — into IndexedDB, and as a synchronous snapshot in
 * `localStorage` so a page reload can decide what to render before Dexie has
 * even opened. Every caller of this function is a genuine unlock (sign-in, PIN
 * setup, PIN entry, resume), which is what stamps the unlock window.
 */
export async function saveSession(session: AuthSession, now?: number): Promise<AuthSession> {
  const stored: AuthSession = { ...session, id: SESSION_ID };
  await db.sessions.put(stored);
  writeSessionCache(stored, { now });
  return stored;
}

/** The active session, or `null` when there is none / it has expired. */
export async function loadSession(now: number = Date.now()): Promise<AuthSession | null> {
  const session = (await db.sessions.get(SESSION_ID)) ?? null;
  if (!session) {
    clearSessionCache();
    return null;
  }
  if (isSessionExpired(session, now)) {
    await clearSession();
    return null;
  }
  return session;
}

/**
 * Opens a session — the one door into the application, and the place the
 * two-tier operator model is actually enforced.
 *
 * A non-operator account throws rather than returning `null`: every caller here
 * hands the result straight to the shell, and a caller that forgot to check
 * would sign a נהג in on a `null` it never looked at. A throw cannot be ignored
 * by accident.
 */
export async function startSession(
  user: User,
  device: Device,
  unlockMethod: UnlockMethod = 'otp',
): Promise<AuthSession> {
  if (!isOperator(user)) throw new NotAnOperatorError(user?.role ?? '');
  return saveSession(createSession(user, device, { unlockMethod }));
}

/** Refreshes `lastSeenAt` after a successful unlock. */
export async function refreshSession(
  unlockMethod?: UnlockMethod,
  now?: number,
): Promise<AuthSession | null> {
  const session = await loadSession(now);
  if (!session) return null;
  return saveSession(touchSession(session, { now, unlockMethod }), now);
}

export async function clearSession(): Promise<void> {
  await db.sessions.delete(SESSION_ID);
  clearSessionCache();
}

/**
 * The result of rehydrating on boot — a page load, or a mobile
 * pull-to-refresh, which the browser serves as exactly the same thing.
 */
export interface RestoredSession {
  user: User;
  session: AuthSession;
  /**
   * The stored unlock is still inside its window: the operator was signed in a
   * moment ago and must go straight back to the app, never to the PIN screen.
   */
  resumable: boolean;
}

/**
 * Reads the active session back before any route guard runs.
 *
 * Returns `null` only when there genuinely is no usable session — none stored,
 * expired, the account was suspended, or it was issued to another device. In
 * every other case the caller gets the signed-in user back, together with the
 * verdict on whether the PIN has to be asked for again.
 */
export async function restoreSession(now: number = Date.now()): Promise<RestoredSession | null> {
  const session = await loadSession(now);
  if (!session) return null;

  const [user, fingerprint] = await Promise.all([
    getUserById(session.userId),
    computeDeviceFingerprint(),
  ]);
  // The operator rule applies to a session that was already on disk as well as
  // to one being opened: a device that signed a נהג in under an older build must
  // not come back up inside the app after an update.
  if (!user || !isOperator(user) || !isSessionValidFor(session, user, fingerprint, now)) {
    await clearSession();
    return null;
  }

  const resumable = canResumeWithoutUnlock(readSessionCache(), session, fingerprint, now);
  return { user, session, resumable };
}

/**
 * Keeps the unlock window following real use — called when the shell mounts
 * and whenever it returns to the foreground, so a refresh after a long working
 * day still resumes.
 */
export function touchActivity(now?: number): void {
  touchSessionCache({ now });
}

/** "נעילה": keeps the session, closes the unlock window. */
export function lockSession(): void {
  lockSessionCache();
}

// ── Local PIN ────────────────────────────────────────────────────────

export async function setLocalPin(userId: string, pin: string): Promise<LocalPinRecord> {
  if (!isValidPin(pin)) throw new Error('PIN must be exactly 4 digits');
  const record = await hashPin(userId, pin);
  await db.pins.put(record);
  return record;
}

export async function hasLocalPin(userId: string): Promise<boolean> {
  return (await db.pins.get(userId)) !== undefined;
}

export async function verifyLocalPin(userId: string, pin: string): Promise<boolean> {
  const record = (await db.pins.get(userId)) ?? null;
  return verifyPin(pin, record);
}

export async function clearLocalPin(userId: string): Promise<void> {
  await db.pins.delete(userId);
}

// ── Maintenance ──────────────────────────────────────────────────────

/** Wipes every auth table — used by the "reset local data" action. */
export async function resetAuthData(): Promise<void> {
  await Promise.all([db.sessions.clear(), db.pins.clear(), db.devices.clear(), db.users.clear()]);
}
